/** Move a floating notice without inline styles; keep its controls inside the viewport. */
export function makeNoticeMovable(dialog: HTMLDialogElement, handle: HTMLElement): () => void {
  let positionAnimation: Animation | undefined
  let frame: number | undefined
  let x = 0
  let y = 0
  let drag: { id: number; offsetX: number; offsetY: number } | undefined

  const move = (nextX: number, nextY: number) => {
    const bounds = dialog.getBoundingClientRect()
    x = Math.max(8, Math.min(nextX, window.innerWidth - bounds.width - 8))
    y = Math.max(8, Math.min(nextY, window.innerHeight - bounds.height - 8))
    const base = getComputedStyle(dialog)
    const transform = `translate(${x - parseFloat(base.left)}px, ${y - parseFloat(base.top)}px)`
    // A held animation applies the position without adding style attributes or CSS rules.
    const previous = positionAnimation
    positionAnimation = dialog.animate([{ transform }, { transform }], { duration: 0, fill: 'forwards' })
    previous?.cancel()
  }

  const start = (event: PointerEvent) => {
    if (event.button !== 0 || drag) return
    const bounds = dialog.getBoundingClientRect()
    drag = { id: event.pointerId, offsetX: event.clientX - bounds.left, offsetY: event.clientY - bounds.top }
    handle.setPointerCapture(event.pointerId)
    handle.classList.replace('cursor-grab', 'cursor-grabbing')
  }
  const update = (event: PointerEvent) => {
    if (!drag || drag.id !== event.pointerId) return
    const nextX = event.clientX - drag.offsetX
    const nextY = event.clientY - drag.offsetY
    if (frame !== undefined) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => { frame = undefined; move(nextX, nextY) })
  }
  const finish = (event: PointerEvent) => {
    if (drag?.id !== event.pointerId) return
    drag = undefined
    handle.classList.replace('cursor-grabbing', 'cursor-grab')
    if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId)
  }
  const keyboard = (event: KeyboardEvent) => {
    const directions: Record<string, readonly [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
    const direction = directions[event.key]
    if (!direction) return
    event.preventDefault()
    const step = event.shiftKey ? 40 : 10
    move(x + direction[0] * step, y + direction[1] * step)
  }
  const resize = () => move(x, y)
  handle.addEventListener('pointerdown', start)
  handle.addEventListener('pointermove', update)
  handle.addEventListener('pointerup', finish)
  handle.addEventListener('pointercancel', finish)
  handle.addEventListener('lostpointercapture', finish)
  handle.addEventListener('keydown', keyboard)
  window.addEventListener('resize', resize)
  move(window.innerWidth - dialog.getBoundingClientRect().width - 24, 96)

  return () => {
    if (frame !== undefined) cancelAnimationFrame(frame)
    positionAnimation?.cancel()
    handle.removeEventListener('pointerdown', start)
    handle.removeEventListener('pointermove', update)
    handle.removeEventListener('pointerup', finish)
    handle.removeEventListener('pointercancel', finish)
    handle.removeEventListener('lostpointercapture', finish)
    handle.removeEventListener('keydown', keyboard)
    window.removeEventListener('resize', resize)
  }
}
